import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import landingPages from '@/shared/config/landing-pages.json';
import toolGuides from '@/shared/config/tool-guides.json';
import { APP_VERSION } from '@/shared/config/app-version';
import { TOOLS } from '@/shared/config/tools';
import seoIndexingPolicy from '@/shared/config/seo-indexing-policy.json';
import {
  HOME_DESCRIPTION,
  HOME_TITLE,
  SITE_NAME,
  SITE_URL,
  canonicalUrl,
  getToolSeo,
} from '@/shared/seo';

type LandingPage = (typeof landingPages)[number];

const STATIC_PAGES: Record<string, { title: string; description: string }> = {
  '/all-tools': {
    title: `All Online Developer Tools | ${SITE_NAME}`,
    description:
      'Browse every EasyUtils browser-based converter, encoder, generator, image, text, data, security, and network tool.',
  },
  '/about': {
    title: `About ${SITE_NAME} | Oxbit Labs`,
    description:
      'Learn how Oxbit Labs builds EasyUtils, a practical collection of free browser-based developer and conversion tools.',
  },
  '/privacy': {
    title: `Privacy Policy | ${SITE_NAME}`,
    description:
      'Read how EasyUtils handles browser data, network tools, analytics, and local storage.',
  },
  '/terms': {
    title: `Terms of Use | ${SITE_NAME}`,
    description: 'Terms and responsible-use requirements for EasyUtils tools.',
  },
  '/changelog': {
    title: `Changelog | ${SITE_NAME}`,
    description: 'Recent EasyUtils product, SEO, route, and tool improvements.',
  },
};

function jsonLdForPage(
  title: string,
  description: string,
  canonical: string,
  isTool: boolean,
  faq?: { question: string; answer: string }[],
) {
  const isHome = canonical === `${SITE_URL}/`;
  const page = isTool
    ? {
        '@type': 'WebApplication',
        name: title,
        url: canonical,
        description,
        applicationCategory: 'DeveloperApplication',
        softwareVersion: APP_VERSION,
        operatingSystem: 'Web',
        browserRequirements: 'Requires a modern web browser',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        publisher: {
          '@type': 'Organization',
          name: 'Oxbit Labs',
          url: 'https://oxbitlabs.com',
        },
      }
    : {
        '@type': isHome ? 'WebSite' : 'WebPage',
        name: title,
        url: canonical,
        description,
        ...(isHome
          ? {
              publisher: {
                '@type': 'Organization',
                name: 'Oxbit Labs',
                url: 'https://oxbitlabs.com',
              },
            }
          : {
              isPartOf: {
                '@type': 'WebSite',
                name: SITE_NAME,
                url: `${SITE_URL}/`,
              },
            }),
      };

  const graph: Record<string, unknown>[] = [
    page,
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: SITE_NAME,
          item: `${SITE_URL}/`,
        },
        ...(canonical === `${SITE_URL}/`
          ? []
          : [{ '@type': 'ListItem', position: 2, name: title, item: canonical }]),
      ],
    },
  ];

  if (faq && faq.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

type RouteFaq = { question: string; answer: string }[] | undefined;

function routeFaq(toolId: string | undefined, landing: LandingPage | undefined): RouteFaq {
  if (landing && 'faq' in landing) return landing.faq;
  if (toolId) {
    const guide = (toolGuides as Record<string, { faq?: { question: string; answer: string }[] }>)[
      toolId
    ];
    return guide?.faq;
  }
  return undefined;
}

export function RouteSeo() {
  const { pathname } = useLocation();
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const tool = TOOLS.find((entry) => entry.path === normalizedPath);
  const landing = landingPages.find((entry) => entry.path === normalizedPath) as
    | LandingPage
    | undefined;
  const staticPage = STATIC_PAGES[normalizedPath];
  const toolCohort = tool
    ? (seoIndexingPolicy.cohortAssignments as Record<string, number>)[tool.id]
    : undefined;
  const isIndexable =
    normalizedPath === '/' ||
    seoIndexingPolicy.indexableStaticPaths.includes(normalizedPath) ||
    Boolean(landing && landing.guide) ||
    Boolean(
      tool &&
      toolCohort !== undefined &&
      seoIndexingPolicy.activeCohorts.includes(toolCohort) &&
      tool.id in (toolGuides as Record<string, unknown>),
    );

  const seo = (() => {
    if (tool) return getToolSeo(tool);
    if (landing) {
      return {
        title: `${landing.title} Online | ${SITE_NAME}`,
        description: landing.description,
        canonicalPath: landing.path,
      };
    }
    if (staticPage) return { ...staticPage, canonicalPath: normalizedPath };
    if (normalizedPath === '/') {
      return { title: HOME_TITLE, description: HOME_DESCRIPTION, canonicalPath: '/' };
    }
    return {
      title: `Page Not Found | ${SITE_NAME}`,
      description: 'The requested EasyUtils page could not be found.',
      canonicalPath: '/404',
    };
  })();

  const canonical = canonicalUrl(seo.canonicalPath);
  const structuredData = jsonLdForPage(
    seo.title,
    seo.description,
    canonical,
    Boolean(tool || landing),
    routeFaq(tool?.id, landing),
  );

  return (
    <Helmet>
      <html lang="en" />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="robots" content={isIndexable ? 'index, follow' : 'noindex, follow'} />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}
